import React from 'react';
import { Cpu, Users, Store, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

const PlatformOverview = () => {
  const products = [
    {
      icon: Cpu,
      name: 'Traain Engine',
      tagline: 'Intelligent Training, Built to Scale',
      description: 'Traain Engine transforms static training into adaptive, AI-guided learning.',
      features: [
        'Turn SOPs, decks, and documentation into structured training',
        'Standardize onboarding and ongoing development',
        'Track completion, readiness, and adoption across teams',
        'Maintain consistency across locations, roles, and regions'
      ],
      aiNote: 'Navigator AI™ helps guide training creation and keeps content aligned as your organization evolves.',
      link: '/products/engine',
      linkText: 'Explore Traain Engine',
      image: 'https://customer-assets.emergentagent.com/job_f6991d0e-ab71-4b45-956d-bb71024e7667/artifacts/43k1hzko_Engine%20Icon.png'
    },
    {
      icon: Users,
      name: 'Traain Cadence',
      tagline: 'Practice That Drives Performance',
      description: 'Traain Cadence delivers realistic, AI-powered role-play so teams don\'t just learn — they perform.',
      features: [
        'Simulate real conversations and scenarios',
        'Provide immediate feedback and scoring',
        'Build confidence before live execution',
        'Reduce coaching time while improving outcomes'
      ],
      aiNote: 'Navigator AI™ evaluates communication, decision-making, and consistency — giving leaders insight without micromanagement.',
      link: '/products/cadence',
      linkText: 'Explore Traain Cadence',
      image: 'https://customer-assets.emergentagent.com/job_f6991d0e-ab71-4b45-956d-bb71024e7667/artifacts/ijiaa7g5_Cadence.png'
    },
    {
      icon: Store,
      name: 'Traain Exchange',
      tagline: 'Expand Capability Without Rebuilding',
      description: 'Traain Exchange is your marketplace for scalable training.',
      features: [
        'Access ready-made training libraries',
        'Purchase role-specific and industry-aligned content',
        'Add new programs instantly without internal rebuilds',
        'Optional white-labeled and custom content options'
      ],
      aiNote: 'Navigator AI™ ensures content integrates seamlessly into your existing enablement flow.',
      link: '/products/exchange',
      linkText: 'Explore Traain Exchange',
      image: 'https://customer-assets.emergentagent.com/job_f6991d0e-ab71-4b45-956d-bb71024e7667/artifacts/yei0p5wf_Exchange%20Icon.png'
    }
  ];

  return (
    <section className="py-24 px-6 bg-gradient-to-b from-transparent to-blue-950/20">
      <div className="max-w-7xl mx-auto">
        <div className="space-y-16">
          {products.map((product, index) => {
            const Icon = product.icon;
            return (
              <div
                key={index}
                className="relative p-8 md:p-12 rounded-3xl bg-gradient-to-br from-gray-900/90 to-blue-900/30 border border-cyan-500/30 overflow-hidden"
              >
                {/* Product Image - Top Right */}
                {product.image && (
                  <div className="hidden sm:block absolute top-4 right-4 md:top-6 md:right-6 w-32 h-32 md:w-48 md:h-48 lg:w-56 lg:h-56 rounded-2xl overflow-hidden shadow-2xl border border-cyan-500/30">
                    <img 
                      src={product.image} 
                      alt={`${product.name} illustration`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                <div className={`flex items-start gap-4 sm:gap-6 mb-8 ${product.image ? 'sm:pr-36 md:pr-56 lg:pr-72' : ''}`}>
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-3xl md:text-4xl font-bold text-white mb-2">
                      {product.name}
                    </h3>
                    <p className="text-xl text-cyan-400 font-semibold">
                      {product.tagline}
                    </p>
                  </div>
                </div>

                <p className={`text-lg text-gray-300 mb-8 leading-relaxed ${product.image ? 'sm:pr-36 md:pr-56 lg:pr-72' : ''}`}>
                  {product.description}
                </p>

                <div className={`grid md:grid-cols-2 gap-4 mb-8 ${product.image ? 'sm:pr-36 md:pr-56 lg:pr-72' : ''}`}>
                  {product.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0" />
                      <span className="text-gray-300">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="mb-8 p-4 rounded-xl bg-cyan-900/20 border border-cyan-500/20">
                  <p className="text-gray-300 italic">
                    {product.aiNote}
                  </p>
                </div>

                <a href={product.link}>
                  <Button
                    variant="outline"
                    className="border-2 border-cyan-500 text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-400"
                  >
                    {product.linkText}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </a>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PlatformOverview;
